import { useCallback, useMemo, useRef, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useLandingPage } from '#src/app/pages/search/shared/hooks';
import { useSearchActions, useSearchResults, useSearchState } from '#src/app/pages/search/shared/searchState';
import { useFocusFallback } from '#src/app/shared/hooks/focus';

type Result = ModelHookResult<
	{
		isLoading: boolean;
		inputRef: React.RefObject<HTMLInputElement | null>;
		inputValue: string;
	},
	{
		setQuery: (newQuery: string) => void;
		clearQuery: () => void;
		submitSearch: () => void;
	}
>;

export function useSearchBox(): Result {
	const track = useTracking();

	const state = useSearchState();
	const results = useSearchResults();
	const actions = useSearchActions();
	const {
		data: { showLandingPage },
	} = useLandingPage();
	const [inputValue, setInputValue] = useState(state.query);
	const inputRef = useRef(null);

	useFocusFallback(inputRef, ({ focus, container }) => focus(container, { preventScroll: true }));
	const isLoading = useMemo(() => results.isLoading || results.isInitialLoad, [results]);

	return {
		data: {
			inputRef,
			isLoading,
			inputValue,
		},
		actions: {
			submitSearch: useCallback(() => {
				if (inputValue) {
					actions.setQuery(inputValue);
				} else {
					actions.clearQuery();
				}
				void track('search', { q: inputValue, context: 'search_term_entered' });
			}, [inputValue, actions, track]),
			setQuery: useCallback((newQuery) => {
				setInputValue(newQuery);
			}, []),
			clearQuery: useCallback(() => {
				setInputValue('');
				if (!showLandingPage && state.query) {
					actions.clearQuery();
				}
				void track('search', { q: state.query, context: 'clear_search_button' });
			}, [state.query, track, showLandingPage, actions]),
		},
	};
}
